/**
 * RBAC (Role-Based Access Control) 헬퍼 함수 구현
 *
 * nGle-Works ERP의 역할 기반 접근 제어 시스템에서 사용되는
 * 권한 체크 함수들의 실제 구현입니다.
 *
 * 모든 함수는 AuthContext를 기반으로 동작하며,
 * system_admin은 모든 권한에 대해 true를 반환합니다.
 *
 * **V111: 레거시 roleId 완전 제거**
 * - super_admin, hr, hr_admin 레거시 roleId 제거
 * - 기준 roleId: system_admin (관리자), role_hr (HR)
 */

import { RoleId, isOrgScopedRole, ADMIN_LIKE_ROLES, AdminLikeRoleId } from '../types/role';
import { RoleAssignment } from '../types/roleAssignment';
import {
  PermissionKey,
  ROLE_PERMISSIONS,
  ROUTE_PERMISSIONS,
} from '../types/permission';
import { AuthContext } from '../types/rbac';

/**
 * V29: 사용자가 관리자급 역할을 가지고 있는지 확인
 *
 * 관리자급 역할 (ADMIN_LIKE_ROLES):
 * - system_admin: 시스템 관리자 (전체 회사)
 * - role_admin_kr: KR 회사 관리자
 * - role_admin_cn: CN 회사 관리자
 *
 * 이 함수는 auth_roleAssignments 기반으로만 동작합니다.
 *
 * @param ctx - 인증 컨텍스트
 * @returns 관리자급 역할이 하나라도 있으면 true
 *
 * @example
 * if (isAdminLike(ctx)) {
 *   // 관리자 기능 허용 (회사 스코프는 백엔드에서 강제)
 *   return true;
 * }
 */
export function isAdminLike(ctx: AuthContext): boolean {
  return ctx.roleAssignments.some(
    (assignment) =>
      ADMIN_LIKE_ROLES.includes(assignment.roleId as AdminLikeRoleId) &&
      assignment.isActive
  );
}

/**
 * 사용자가 System Admin인지 확인
 *
 * **V29 변경:** isAdminLike()를 사용하여 관리자급 역할 전체를 체크합니다.
 * - system_admin, role_admin_kr, role_admin_cn 중 하나라도 있으면 true
 *
 * 기존 isSuperAdmin() 호출 코드와 호환성을 유지하면서,
 * role_admin_kr/cn도 동일하게 처리됩니다.
 *
 * @param ctx - 인증 컨텍스트
 * @returns 관리자급 역할이면 true
 *
 * @example
 * if (isSuperAdmin(ctx)) {
 *   // 모든 권한 허용 (관리자급)
 *   return true;
 * }
 */
export function isSuperAdmin(ctx: AuthContext): boolean {
  // V29: system_admin 하드코딩 제거 → isAdminLike() 사용
  return isAdminLike(ctx);
}

/**
 * 사용자가 HR 또는 관리자급인지 확인
 *
 * 인사 관련 기능에서 자주 사용되는 헬퍼 함수입니다.
 *
 * **V29 변경:** isAdminLike()를 사용하여 관리자급 역할 전체를 포함합니다.
 * - role_hr: HR 담당자
 * - system_admin, role_admin_kr, role_admin_cn: 관리자급
 *
 * @param ctx - 인증 컨텍스트
 * @returns HR 또는 관리자급이면 true
 *
 * @example
 * if (isHrOrAdmin(ctx)) {
 *   // 인사 데이터 접근 허용
 * }
 */
export function isHrOrAdmin(ctx: AuthContext): boolean {
  // V29: 관리자급(AdminLike)이면 true
  if (isAdminLike(ctx)) {
    return true;
  }
  // role_hr 체크
  return ctx.roleAssignments.some(
    (assignment) =>
      assignment.roleId === 'role_hr' && assignment.isActive
  );
}

/**
 * 사용자가 특정 역할을 가지고 있는지 확인
 *
 * 역할 보유 여부를 체크하는 가장 기본적인 함수입니다.
 *
 * **처리 로직:**
 * 1. Super Admin이면 항상 true 반환
 * 2. 조직 범위 역할(org_manager, approver)인 경우:
 *    - orgUnitId가 제공되면: 해당 조직에 대한 역할이 있는지 확인
 *    - orgUnitId가 없으면: 어떤 조직이든 해당 역할이 있으면 true
 * 3. 일반 역할인 경우: 역할 할당이 있는지만 확인
 *
 * **조직 범위 역할 처리:**
 * org_manager와 approver는 특정 조직에 대해서만 권한을 가집니다.
 * 예: 개발팀(org_team_dev)의 org_manager는 개발팀만 관리 가능
 *
 * @param ctx - 인증 컨텍스트
 * @param roleId - 확인할 역할 ID
 * @param orgUnitId - 조직 기반 역할인 경우 조직 ID (선택)
 * @returns 역할을 보유하고 있으면 true
 *
 * @example
 * // 일반 역할 확인
 * hasRole(ctx, 'role_hr') // HR 역할이 있는지 확인
 *
 * @example
 * // 조직 기반 역할 확인
 * hasRole(ctx, 'org_manager', 'org_team_dev') // 개발팀 조직장인지 확인
 */
export function hasRole(
  ctx: AuthContext,
  roleId: RoleId,
  orgUnitId?: string
): boolean {
  // Super Admin은 모든 역할을 가진 것으로 간주
  if (isSuperAdmin(ctx)) {
    return true;
  }

  // 조직 범위 역할인지 확인
  const isOrgScoped = isOrgScopedRole(roleId);

  // 사용자의 역할 할당 중에서 조건에 맞는 것이 있는지 확인
  return ctx.roleAssignments.some((assignment) => {
    // 비활성화된 역할은 제외
    if (!assignment.isActive) {
      return false;
    }

    // 역할 ID가 일치하지 않으면 제외
    if (assignment.roleId !== roleId) {
      return false;
    }

    // 조직 범위 역할인 경우
    if (isOrgScoped) {
      // orgUnitId가 제공되지 않은 경우: 어떤 조직이든 해당 역할이 있으면 true
      if (!orgUnitId) {
        return true;
      }

      // orgUnitId가 제공된 경우: 해당 조직에 대한 역할이 있는지 확인
      // TODO: 향후 하위 조직까지 포함하려면 조직 계층 구조를 확인해야 함
      return assignment.orgUnitId === orgUnitId;
    }

    // 일반 역할인 경우: 역할만 있으면 true
    return true;
  });
}

/**
 * 사용자가 여러 역할 중 하나라도 가지고 있는지 확인
 *
 * OR 조건으로 역할을 체크할 때 사용합니다.
 * 예: HR 또는 임원인지 확인
 *
 * @param ctx - 인증 컨텍스트
 * @param roleIds - 확인할 역할 ID 목록
 * @param orgUnitId - 조직 기반 역할인 경우 조직 ID (선택)
 * @returns 하나라도 보유하고 있으면 true
 *
 * @example
 * // HR 또는 System Admin인지 확인
 * hasAnyRole(ctx, ['role_hr', 'system_admin'])
 *
 * @example
 * // 개발팀의 조직장 또는 승인자인지 확인
 * hasAnyRole(ctx, ['org_manager', 'approver'], 'org_team_dev')
 */
export function hasAnyRole(
  ctx: AuthContext,
  roleIds: RoleId[],
  orgUnitId?: string
): boolean {
  // 주어진 역할 중 하나라도 있으면 true
  return roleIds.some((roleId) => hasRole(ctx, roleId, orgUnitId));
}

/**
 * 사용자가 여러 역할을 모두 가지고 있는지 확인
 *
 * AND 조건으로 역할을 체크할 때 사용합니다.
 * 예: 조직장이면서 동시에 승인자인지 확인
 *
 * @param ctx - 인증 컨텍스트
 * @param roleIds - 확인할 역할 ID 목록
 * @param orgUnitId - 조직 기반 역할인 경우 조직 ID (선택)
 * @returns 모두 보유하고 있으면 true
 *
 * @example
 * // 조직장이면서 승인자인지 확인
 * hasAllRoles(ctx, ['org_manager', 'approver'], 'org_team_dev')
 */
export function hasAllRoles(
  ctx: AuthContext,
  roleIds: RoleId[],
  orgUnitId?: string
): boolean {
  // 주어진 역할을 모두 가지고 있어야 true
  return roleIds.every((roleId) => hasRole(ctx, roleId, orgUnitId));
}

/**
 * 사용자가 가진 모든 역할 ID 목록 조회
 *
 * 사용자에게 할당된 활성 역할의 ID 목록을 반환합니다.
 * 중복 제거된 배열을 반환합니다.
 *
 * @param ctx - 인증 컨텍스트
 * @returns 역할 ID 배열
 *
 * @example
 * const roles = getUserRoles(ctx);
 * // ['general', 'org_manager', 'approver']
 */
export function getUserRoles(ctx: AuthContext): RoleId[] {
  // 활성화된 역할 할당에서 역할 ID만 추출
  const roleIds = ctx.roleAssignments
    .filter((assignment) => assignment.isActive)
    .map((assignment) => assignment.roleId);

  // 중복 제거
  return Array.from(new Set(roleIds));
}

/**
 * 사용자가 가진 모든 권한 목록 조회
 *
 * **V167: DB 기반 권한 SSoT**
 * - AuthContext.permissions가 있으면 우선 사용 (DB 기반)
 * - 없으면 ROLE_PERMISSIONS 폴백 (하드코딩, 레거시/DEMO 모드)
 *
 * 중복은 자동으로 제거됩니다.
 *
 * **처리 로직:**
 * 1. ctx.permissions가 있으면 바로 반환 (V167 DB 기반)
 * 2. 없으면 기존 ROLE_PERMISSIONS 기반 로직 사용 (폴백)
 * 3. 조직 범위 역할의 경우:
 *    - orgUnitId 옵션이 제공된 경우: 해당 조직에 대한 역할만 포함
 *    - orgUnitId 옵션이 없는 경우: 모든 조직에 대한 역할 포함
 *
 * @param ctx - 인증 컨텍스트
 * @param options - 추가 옵션
 * @param options.orgUnitId - 조직 범위 권한 체크 시 사용할 조직 ID
 * @returns 권한 키 배열 (중복 제거됨)
 *
 * @example
 * // 사용자의 모든 권한 조회
 * const permissions = getUserPermissions(ctx);
 * // ['dashboard.read', 'notice.read', 'work.my.read', ...]
 *
 * @example
 * // 특정 조직에 대한 권한만 조회
 * const permissions = getUserPermissions(ctx, { orgUnitId: 'org_team_dev' });
 */
export function getUserPermissions(
  ctx: AuthContext,
  options?: { orgUnitId?: string }
): PermissionKey[] {
  // V167 비활성화: DB의 auth_roles.permissions와 프론트 ROLE_PERMISSIONS 형식이 다름
  // - DB: 'users.read.own', 'hr.read.all' (Cloud Functions 권한용)
  // - 프론트: 'dashboard.read', 'hr.manage' (메뉴 접근용)
  // 향후 DB 권한 형식 통일 후 재활성화 필요
  // if (ctx.permissions && ctx.permissions.length > 0) {
  //   return ctx.permissions as PermissionKey[];
  // }

  // ROLE_PERMISSIONS 기반 (현재 유일한 동작 모드)
  const allPermissions: PermissionKey[] = [];

  // 각 역할 할당에 대해 권한 수집
  ctx.roleAssignments.forEach((assignment) => {
    // 비활성화된 역할은 제외
    if (!assignment.isActive) {
      return;
    }

    // 조직 범위 역할 처리
    if (isOrgScopedRole(assignment.roleId)) {
      // orgUnitId 옵션이 제공된 경우
      if (options?.orgUnitId) {
        // 해당 조직에 대한 역할 할당이 아니면 제외
        // TODO: 향후 하위 조직까지 포함하려면 조직 계층 구조 확인 필요
        if (assignment.orgUnitId !== options.orgUnitId) {
          return;
        }
      }
      // orgUnitId 옵션이 없으면 모든 조직에 대한 역할 포함
    }

    // 역할에 해당하는 권한 목록 가져오기
    const rolePermissions = ROLE_PERMISSIONS[assignment.roleId] || [];

    if (rolePermissions.length === 0) {
    }

    allPermissions.push(...rolePermissions);
  });

  // 중복 제거
  const uniquePermissions = Array.from(new Set(allPermissions));

  return uniquePermissions;
}

/**
 * 사용자가 특정 권한을 가지고 있는지 확인
 *
 * 권한 체크의 핵심 함수입니다.
 * 사용자의 모든 역할을 확인하여 하나라도 해당 권한을 가지면 true를 반환합니다.
 *
 * **처리 로직:**
 * 1. Super Admin이면 항상 true 반환
 * 2. getUserPermissions()를 호출하여 사용자의 모든 권한 조회
 * 3. 조회된 권한 목록에 요청한 권한이 포함되어 있는지 확인
 *
 * **조직 범위 권한:**
 * 조직 기반 역할(org_manager, approver)의 권한은
 * orgUnitId 옵션을 제공해야 정확하게 체크됩니다.
 *
 * @param ctx - 인증 컨텍스트
 * @param permission - 확인할 권한 키
 * @param options - 추가 옵션
 * @param options.orgUnitId - 조직 범위 권한 체크 시 사용할 조직 ID
 * @returns 권한을 보유하고 있으면 true
 *
 * @example
 * // 공지사항 작성 권한 확인
 * if (hasPermission(ctx, 'notice.write')) {
 *   // 공지사항 작성 허용
 * }
 *
 * @example
 * // 특정 조직의 팀 관리 권한 확인
 * if (hasPermission(ctx, 'team.my.manage', { orgUnitId: 'org_team_dev' })) {
 *   // 개발팀 관리 허용
 * }
 */
export function hasPermission(
  ctx: AuthContext,
  permission: PermissionKey,
  options?: { orgUnitId?: string }
): boolean {
  // Super Admin은 모든 권한을 가짐
  if (isSuperAdmin(ctx)) {
    return true;
  }

  // 사용자의 모든 권한 조회
  const userPermissions = getUserPermissions(ctx, options);

  // 요청한 권한이 포함되어 있는지 확인
  return userPermissions.includes(permission);
}

/**
 * 사용자가 특정 라우트에 접근 가능한지 확인
 *
 * 라우트 경로를 기반으로 접근 권한을 체크합니다.
 * ROUTE_PERMISSIONS 상수에 정의된 라우트별 필요 권한을 확인합니다.
 *
 * **처리 로직:**
 * 1. Super Admin이면 항상 true 반환
 * 2. ROUTE_PERMISSIONS에서 해당 라우트의 필요 권한 조회
 * 3. 필요 권한이 정의되지 않은 라우트는 기본적으로 접근 허용 (true)
 * 4. 필요 권한이 하나인 경우: 해당 권한 체크
 * 5. 필요 권한이 여러 개인 경우: 하나라도 있으면 접근 허용 (OR 조건)
 *
 * **라우트 매칭 전략:**
 * - 정확한 경로 매칭을 우선 시도
 * - 동적 파라미터가 있는 경로는 ROUTE_PERMISSIONS에 패턴으로 등록
 *   예: '/users/:id' → ROUTE_PERMISSIONS['/users/:id']
 *
 * **DEMO 모드 주의사항:**
 * - 새로운 라우트를 추가하면 ROUTE_PERMISSIONS에도 등록해야 함
 * - 권한이 정의되지 않은 라우트는 모두 접근 가능하므로 주의
 *
 * @param ctx - 인증 컨텍스트
 * @param routePath - 확인할 라우트 경로 (예: '/hr/employees')
 * @param options - 추가 옵션
 * @param options.orgUnitId - 조직 범위 권한 체크 시 사용할 조직 ID
 * @returns 접근 가능하면 true
 *
 * @example
 * // 인사관리 페이지 접근 가능 여부 확인
 * if (canAccessRoute(ctx, '/hr/employees')) {
 *   // 페이지 표시
 * } else {
 *   // 권한 없음 메시지 표시
 * }
 *
 * @example
 * // 특정 조직의 팀 관리 페이지 접근 확인
 * if (canAccessRoute(ctx, '/team/management', { orgUnitId: 'org_team_dev' })) {
 *   // 페이지 표시
 * }
 */
export function canAccessRoute(
  ctx: AuthContext,
  routePath: string,
  options?: { orgUnitId?: string }
): boolean {
  // Super Admin은 모든 라우트 접근 가능
  const isAdmin = isSuperAdmin(ctx);
  if (isAdmin) {
    return true;
  }

  // 라우트에 필요한 권한 조회
  const requiredPermission = ROUTE_PERMISSIONS[routePath];

  // 권한이 정의되지 않은 라우트는 접근 허용
  // TODO: 실제 운영 시에는 기본값을 false로 변경하여 명시적으로 권한을 정의하도록 강제
  if (!requiredPermission) {
    return true;
  }

  // 필요 권한이 배열인 경우 (여러 권한 중 하나만 있으면 됨 - OR 조건)
  if (Array.isArray(requiredPermission)) {
    const result = requiredPermission.some((permission) =>
      hasPermission(ctx, permission, options)
    );
    return result;
  }

  // 필요 권한이 하나인 경우
  const result = hasPermission(ctx, requiredPermission, options);
  return result;
}

/**
 * 사용자가 특정 조직을 관리할 수 있는지 확인
 *
 * 조직 관리 권한을 체크합니다.
 * org_manager, role_hr, system_admin만 조직을 관리할 수 있습니다.
 *
 * **처리 로직:**
 * 1. Super Admin이면 항상 true (모든 조직 관리 가능)
 * 2. HR이면 항상 true (모든 조직 관리 가능)
 * 3. org_manager인 경우:
 *    - 본인이 관리하는 조직인지 확인
 *    - TODO: 향후 하위 조직까지 관리 가능하도록 계층 구조 확인 필요
 *
 * **조직 계층 구조 활용 (향후 구현):**
 * - OrgUnit의 path, parentId, level 필드를 활용
 * - 예: path가 'company/dept_tech/team_dev'인 경우
 *   - 'company/dept_tech'의 org_manager는 'team_dev'도 관리 가능
 *   - 이를 위해 targetOrgUnit.path가 managerOrgUnit.path로 시작하는지 확인
 *
 * @param ctx - 인증 컨텍스트
 * @param targetOrgUnitId - 관리하려는 조직 ID
 * @returns 관리 가능하면 true
 *
 * @example
 * // 개발팀을 관리할 수 있는지 확인
 * if (canManageOrgUnit(ctx, 'org_team_dev')) {
 *   // 조직 정보 수정 허용
 * }
 */
export function canManageOrgUnit(
  ctx: AuthContext,
  targetOrgUnitId: string
): boolean {
  // Super Admin은 모든 조직 관리 가능
  if (isSuperAdmin(ctx)) {
    return true;
  }

  // HR은 모든 조직 관리 가능
  if (isHrOrAdmin(ctx)) {
    return true;
  }

  // org_manager 역할로 해당 조직을 관리할 수 있는지 확인
  const hasOrgManagerRole = ctx.roleAssignments.some((assignment) => {
    if (!assignment.isActive) {
      return false;
    }

    if (assignment.roleId !== 'org_manager') {
      return false;
    }

    // 해당 조직에 대한 org_manager 역할이 있는지 확인
    // TODO: 향후 하위 조직까지 포함하려면:
    // 1. targetOrgUnit의 path를 조회
    // 2. assignment.orgUnitId의 path를 조회
    // 3. targetOrgUnit.path가 assignment.orgUnit.path로 시작하는지 확인
    // 예: assignment.orgUnit.path = 'company/dept_tech'
    //     targetOrgUnit.path = 'company/dept_tech/team_dev'
    //     → true (하위 조직이므로 관리 가능)
    return assignment.orgUnitId === targetOrgUnitId;
  });

  return hasOrgManagerRole;
}

/**
 * 사용자가 조직장인지 확인
 *
 * 특정 조직의 조직장인지 확인합니다.
 * orgUnitId를 제공하지 않으면 어떤 조직이든 조직장이면 true를 반환합니다.
 *
 * @param ctx - 인증 컨텍스트
 * @param orgUnitId - 확인할 조직 ID (선택, 미제공 시 모든 조직)
 * @returns 조직장이면 true
 *
 * @example
 * // 어떤 조직이든 조직장인지 확인
 * if (isOrgManager(ctx)) {
 *   // 조직장 전용 기능 허용
 * }
 *
 * @example
 * // 개발팀 조직장인지 확인
 * if (isOrgManager(ctx, 'org_team_dev')) {
 *   // 개발팀 관련 기능 허용
 * }
 */
export function isOrgManager(ctx: AuthContext, orgUnitId?: string): boolean {
  return hasRole(ctx, 'org_manager', orgUnitId);
}

/**
 * 사용자가 승인자인지 확인
 *
 * 특정 조직의 승인자인지 확인합니다.
 * orgUnitId를 제공하지 않으면 어떤 조직이든 승인자면 true를 반환합니다.
 *
 * @param ctx - 인증 컨텍스트
 * @param orgUnitId - 확인할 조직 ID (선택, 미제공 시 모든 조직)
 * @returns 승인자면 true
 *
 * @example
 * // 어떤 조직이든 승인자인지 확인
 * if (isApprover(ctx)) {
 *   // 승인 관리 메뉴 표시
 * }
 *
 * @example
 * // 개발팀 승인자인지 확인
 * if (isApprover(ctx, 'org_team_dev')) {
 *   // 개발팀 승인 처리 허용
 * }
 */
export function isApprover(ctx: AuthContext, orgUnitId?: string): boolean {
  return hasRole(ctx, 'approver', orgUnitId);
}

/**
 * V36: canViewSensitiveRaw 함수 제거
 *
 * V36에서 암호화/마스킹이 완전 제거되어 항상 원문이 표시됩니다.
 * 민감정보 접근 권한은 Firestore Rules와 Cloud Functions에서 처리됩니다.
 */
